import { useState, useEffect, useImperativeHandle, ReactNode } from 'react';
import type { Ref } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { User } from './mentions';

const MentionListItemText = (props: { bold?: boolean; children: ReactNode }) => (
   <span className={cn('pr-2.5 text-base text-black', props.bold && 'font-bold')}>
      {props.children}
   </span>
);

const MentionListItem = (props: {
   index: number;
   item: User;
   selected: boolean;
   onMouseOver: () => void;
   onClick: () => void;
}) => (
   <li className="overflow-auto text-start">
      <button
         type="button"
         className={cn(
            'flex w-full items-center py-1.5 px-1',
            props.selected ? 'mentionSelected bg-gray-200' : 'bg-white'
         )}
         onClick={props.onClick}
         onMouseOver={props.onMouseOver}
      >
         <img src={props.item.image} alt="" className="relative mt-0.5 mr-2 size-6 rounded-full" />
         <MentionListItemText bold={true}>{props.item.unique_username}</MentionListItemText>
         <MentionListItemText>{props.item.username}</MentionListItemText>
      </button>
   </li>
);

function MentionList({
   ref,
   ...props
}: {
   children?: ReactNode;
   items: User[];
   command: (arg0: object) => void;
   ref?: Ref<unknown>;
}) {
   const [selectedIndex, setSelectedIndex] = useState(0);

   const selectItem = (index: number) => {
      const item = props.items[index];

      if (item) {
         props.command({ id: item.unique_username });
      }
   };

   const upHandler = () => {
      setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
   };

   const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % props.items.length);
   };

   const selectionHandler = () => {
      selectItem(selectedIndex);
   };

   // eslint-disable-next-line react-hooks/set-state-in-effect
   useEffect(() => setSelectedIndex(0), [props.items]);

   useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
         switch (event.key) {
            case 'ArrowUp': {
               upHandler();
               return true;
            }
            case 'ArrowDown': {
               downHandler();
               return true;
            }
            case 'Enter':
            case 'Tab': {
               selectionHandler();
               return true;
            }
            default: {
               return false;
            }
         }
      },
   }));

   return (
      <ul className="m-0 rounded bg-white p-0 shadow-[0_2px_6px_rgba(0,0,0,0.3)]">
         {props.items.map((item: User, index: number) => (
            <MentionListItem
               key={index}
               index={index}
               item={item}
               selected={index === selectedIndex}
               onClick={() => selectItem(index)}
               onMouseOver={() => setSelectedIndex(index)}
            />
         ))}
      </ul>
   );
}

export { MentionList };
